import { fetchAssets, fetchMarkets, formatAssets, formatMarkets } from '@nbx/frontend-helpers/repository';
import { publishAuthAccountClaimsChanged, publishAuthAccountIdChanged } from '@nbx/frontend-helpers/publish';
import { NBXStorage } from '@nbx/frontend-helpers/storage';
import { post } from '@nbx/frontend-helpers/fetch-api';
import { getAuthBaseUrl } from './url';
// eslint-disable-next-line import/no-cycle
import { getSession, publishSessionExpired, setSessionTokensRefreshTimeout } from './session';
import { AccountClaims, Session } from '@nbx/frontend-helpers/types';
import { clearGlobalFetchAccountTokenPromise } from './tokenUtils';
import { isSessionRejected, wait, TRANSIENT_RETRY_DELAYS_MS } from './refreshFailure';

export interface AccountTokenResponse {
  claims: AccountClaims;
  exp: number;
  token: string;
}

export async function refreshAccountToken(
  session: Session,
  activeAccountId: string
  // logoutOn403 = true
): Promise<string> {
  const accountId = activeAccountId ?? session.accountId;
  if (window.fetchAccountTokenPromise && accountId === window.fetchAccountTokenPromiseId) {
    console.log(
      `[helpers.auth.refreshAccountToken] return cached fetchAccountTokenPromise (session.exp=${session?.exp}) for account ${accountId}`
    );
    setTimeout(clearGlobalFetchAccountTokenPromise, 15000);
    return window.fetchAccountTokenPromise;
  }

  window.fetchAccountTokenPromiseId = accountId;
  window.fetchAccountTokenPromise = doRefreshAccountToken(session, accountId);
  return window.fetchAccountTokenPromise;
}

let accountTokenRefreshTimeout: ReturnType<typeof setTimeout>;

export const setAccountTokensDebounceRefresh = (response: AccountTokenResponse): void => {
  if (accountTokenRefreshTimeout) clearTimeout(accountTokenRefreshTimeout);
  accountTokenRefreshTimeout = setTimeout(
    () =>
      Promise.all([getSession(), NBXStorage.getItem('active-account')])
        .then(([session, activeAccountId]) => {
          console.log(`[helpers.auth.accountTokenRefreshTimeout]: activeAccountId=${activeAccountId}`);
          activeAccountId && refreshAccountToken(session, activeAccountId);
        })
        .catch(e =>
          console.warn('setAccountTokensDebounceRefresh: cannot getSession and refreshAccountToken', e)
        ),
    (response.claims.exp - response.claims.iat) * 1000 * 0.65
  );
};

export async function doRefreshAccountToken(
  session: Session,
  accountId: string,
  attempt = 0
): Promise<string> {
  try {
    if (!session) {
      console.log(`[helpers.auth.refreshAccountToken] cannot refresh account token: no session`);
      throw new Error('doRefreshAccountToken: no session');
    }
    const response = (await post(`${getAuthBaseUrl()}/accounts/${accountId}/tokens`, { json: {} }, session, {
      camelizeResponseKeys: false
    })) as AccountTokenResponse;

    NBXStorage.setItem(accountId, response.token);
    publishAuthAccountIdChanged(accountId);
    publishAuthAccountClaimsChanged(response.claims);

    setAccountTokensDebounceRefresh(response);

    try {
      const accountContext = { token: response.token };
      const markets = formatMarkets(await fetchMarkets(accountContext), response.claims.markets);
      const assets = formatAssets(await fetchAssets(accountContext), response.claims.assets);
      if (window.CAN_CACHE_IDB) {
        const time = new Date().getTime();
        window.IDB_PUBLIC_STORE.set('markets', { data: markets, time });
        window.IDB_PUBLIC_STORE.set('assets', { data: assets, time });
      }
    } catch (e) {
      // A breadcrumb, not the verdict: the catch below decides whether this ends the session.
      console.warn(
        `doRefreshAccountToken: cannot fetch markets and assets (response.token=${response?.exp} jti=${response?.claims?.jti})`,
        e
      );
      throw e;
    }

    setSessionTokensRefreshTimeout(session);
    setTimeout(clearGlobalFetchAccountTokenPromise, 15000);
    return response.token;
  } catch (e) {
    if (session && !isSessionRejected(e)) {
      if (attempt < TRANSIENT_RETRY_DELAYS_MS.length) {
        console.log(
          `[helpers.auth.doRefreshAccountToken] no answer from the server, retry ${attempt + 1}/${TRANSIENT_RETRY_DELAYS_MS.length}`,
          e
        );
        await wait(TRANSIENT_RETRY_DELAYS_MS[attempt]);
        return doRefreshAccountToken(session, accountId, attempt + 1);
      }
      // Session kept: the next 401 refreshes on demand, so there is no timer to re-arm here.
      console.warn(`doRefreshAccountToken: giving up for now, session kept (session.exp=${session?.exp})`, e);
      clearGlobalFetchAccountTokenPromise();
      throw e;
    }
    console.warn(
      `doRefreshAccountToken: will logout - session rejected while refreshing the account token using session.exp=${session?.exp}`
    );
    clearGlobalFetchAccountTokenPromise();
    if (window.PLATFORM === 'web') {
      window.LOGOUT();
    } else {
      publishSessionExpired();
    }
    throw e;
  }
}
